/**
 * Early Warning Signals (EWS) Type Definitions
 *
 * These types define the data structures for the EWS dashboard which displays
 * risk analytics and customer cohort management.
 *
 * API Spec: /FinProAdminServer/docs/ews-api-spec.json
 */

// =============================================================================
// Enums and Constants
// =============================================================================

/**
 * Risk level classification for customers and risk factors.
 * Used to determine visual styling (colors) and filtering.
 */
export type RiskLevel = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

/**
 * Risk indicator section/category.
 * Groups risk factors into logical categories.
 */
export type RiskSection =
  | 'INCOME_EMPLOYMENT'
  | 'REPAYMENT_BEHAVIOR'
  | 'DEBT_LEVERAGE'
  | 'CASH_FLOW_LIQUIDITY';

/**
 * Time period options for filtering overview metrics.
 * Controls the date range for aggregated statistics.
 */
export type TimePeriod = 'lastWeek' | 'lastMonth' | 'last6Months' | 'lastYear' | 'custom';

/**
 * Search domain options for customer search.
 * API currently only supports consentId search.
 */
export type SearchDomain = 'consentId' | 'textualQuery';

/**
 * Tab identifiers for the main page navigation.
 */
export type EWSTab = 'overview' | 'customers';

// =============================================================================
// Risk Factor Types
// =============================================================================

/**
 * Individual risk factor with its assessment level and value.
 * Risk factors are dynamic attributes that contribute to the overall risk score.
 *
 * Example:
 * { name: 'EMI Delays', section: 'REPAYMENT_BEHAVIOR', level: 'HIGH', value: 32 }
 */
export interface EWSRiskFactor {
  /** Display name of the risk factor (e.g., "EMI Delays", "Loan Stacking") */
  name: string;

  /** Risk section/category this factor belongs to */
  section?: RiskSection;

  /** Risk level assessment for this factor */
  level: RiskLevel;

  /**
   * The actual value of this factor.
   * Can be a string (e.g., 'changed'), number (e.g., 4), or complex object.
   */
  value: string | number | Record<string, unknown>;

  /**
   * Optional description providing context about the factor.
   * Example: "(Higher is better)" for SIPs
   */
  description?: string;
}

// =============================================================================
// Payment Window Types
// =============================================================================

/**
 * Payment pattern window data showing when payments typically occur.
 * Used for salary, EMI, and SIP payment patterns.
 */
export interface PaymentWindow {
  /** Start day of the payment window (1-31) */
  startDay: number;

  /** End day of the payment window (1-31) */
  endDay: number;

  /** Confidence score for this pattern (0-1) */
  confidence: number;

  /** Average transaction amount */
  averageAmount: number;

  /** Transaction counterparty name */
  counterparty: string;

  /** Transaction category */
  category?: string;

  /** Transaction sub-category */
  subCategory?: string;
}

// =============================================================================
// Customer Types
// =============================================================================

/**
 * Customer list item for table display.
 * Contains basic information for the customers list view.
 */
export interface EWSCustomerListItem {
  /** Unique tracking ID (consent ID) in UUID format */
  trackingId: string;

  /** Current account balance in INR */
  currentBalance: number;

  /** Optimal date to trigger collection based on salary window (YYYY-MM-DD) */
  optimumDayToTrigger: string;

  /** Number of active EMI obligations */
  activeEMIsCount: number;

  /** Number of active SIP investments */
  activeSIPs: number;

  /** Pre-computed risk score (0-40) - not available in Athena */
  riskScore?: number;

  /** Categorized risk level based on risk score */
  riskLevel: RiskLevel;

  /** Fixed Obligation to Income Ratio */
  foir: number;

  /** Optimal day window for max balance trigger */
  odwMaxBalanceWindow?: { startDay: number; endDay: number; averageBalance: number; medianBalance: number; typicalBalance: number; confidence: number }[] | null;
}

/**
 * Complete customer record from the ews_analytics table.
 * Contains all customer data including financial metrics, risk assessment,
 * and detailed risk factors. Extends EWSCustomerListItem with additional details.
 */
export interface EWSCustomer extends EWSCustomerListItem {
  /** Customer mobile number */
  mobileNumber?: string;

  /**
   * Pre-computed risk score (0-40).
   * Thresholds: 0-3 LOW, 4-7 MEDIUM, 8-12 HIGH, 13+ CRITICAL
   */
  riskScore: number;

  /** Categorized risk level based on risk score */
  riskLevel: RiskLevel;

  /** Historical average balance */
  averageBalance: number;

  /** Fixed Obligation to Income Ratio */
  foir: number;

  /** Array of individual risk factors (up to 20 indicators) */
  riskFactors: EWSRiskFactor[];

  /** Salary payment window data */
  salaryWindow?: PaymentWindow[] | null;

  /** EMI payment window data */
  emiWindow?: PaymentWindow[] | null;

  /** SIP payment window data */
  sipWindow?: PaymentWindow[] | null;

  /** Record creation timestamp */
  createdAt?: string;

  // Legacy fields for UI compatibility (mapped from API response)
  /** @deprecated Use trackingId instead */
  consentId?: string;

  /** @deprecated Use activeEMIsCount instead */
  emiCount?: number;

  /** @deprecated Use activeSIPs instead */
  sips?: number;
}

// =============================================================================
// Overview Metrics Types
// =============================================================================

/**
 * Aggregated metrics for the Overview tab.
 * These are calculated server-side based on the selected time period.
 */
export interface EWSOverviewMetrics {
  /** Total number of customers in the cohort */
  totalCustomers: number;

  /** Average balance across all customers (in INR) */
  averageBalance: number;

  /** Average EMI count per customer */
  averageEMICount: number;

  /** Average SIPs per customer */
  averageSIPs: number;

  /** Count of customers classified as HIGH risk */
  highRiskCount: number;

  /** Percentage of customers that are HIGH risk */
  highRiskPercentage: number;

  /** Count of customers in CRITICAL state (HIGH risk + HIGH repayment behavior) */
  criticalCount: number;

  /** Percentage of customers that are CRITICAL */
  criticalPercentage: number;

  /** ISO timestamp of when the data was last refreshed */
  lastRefreshedAt: string;
}

// =============================================================================
// Distribution Types (Analytics)
// =============================================================================

/**
 * Single data point in a risk indicator distribution.
 */
export interface DistributionDataPoint {
  /** User-friendly label for display (e.g., 'No Bounces', 'Some Bounces') */
  label: string;

  /** Risk level for this data point */
  level: RiskLevel;

  /** Number of customers at this level */
  count: number;
}

/**
 * Distribution of customers for a single risk indicator.
 * Used to render bar charts showing customer segmentation.
 */
export interface IndicatorDistribution {
  /** Indicator key (database column prefix) */
  indicator: string;

  /** Display name for the indicator */
  name: string;

  /** Chart description/subtitle */
  description: string;

  /** Risk section/category */
  section: RiskSection;

  /** Distribution data points (LOW, MEDIUM, HIGH) */
  data: DistributionDataPoint[];
}

/**
 * Response data for distributions endpoint.
 */
export interface EWSDistributionsData {
  /** Array of indicator distributions */
  distributions: IndicatorDistribution[];

  /** Total customers in dataset */
  totalCustomers: number;

  /** Timestamp when data was fetched */
  fetchedAt: string;
}

// =============================================================================
// Pagination Types
// =============================================================================

/**
 * Pagination metadata for paginated API responses.
 */
export interface PaginationInfo {
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
}

// =============================================================================
// API Response Types
// =============================================================================

/**
 * Standard API response wrapper.
 */
export interface ApiResponse<T> {
  status: 'success' | 'fail';
  ver: string;
  data?: T;
  message?: string;
  code?: number;
  statusCode?: number;
}

/**
 * Response for overview metrics endpoint.
 */
export interface OverviewApiResponse extends ApiResponse<EWSOverviewMetrics> {}

/**
 * Response data for customers list endpoint.
 */
export interface CustomersListData {
  customers: EWSCustomerListItem[];
  pagination: PaginationInfo;
}

/**
 * Response for customers list endpoint.
 */
export interface CustomersListApiResponse extends ApiResponse<CustomersListData> {}

/**
 * Response for customer detail endpoint.
 */
export interface CustomerDetailApiResponse extends ApiResponse<EWSCustomer> {}

/**
 * Response for distributions endpoint.
 */
export interface DistributionsApiResponse extends ApiResponse<EWSDistributionsData> {}

/**
 * Response for refresh endpoint.
 */
export interface RefreshApiResponse extends ApiResponse<{ refreshedAt: string }> {
  message: string;
}

/**
 * Paginated response wrapper for customer list (internal use).
 */
export interface EWSCustomersResponse {
  data: EWSCustomer[];
  pagination: PaginationInfo;
}

// =============================================================================
// API Request Types
// =============================================================================

/**
 * Valid sort columns for customers list.
 */
export type CustomerSortColumn = 'currentBalance' | 'riskLevel' | 'riskScore' | 'foir' | 'activeEMIsCount' | 'activeSIPs' | 'createdAt';

/**
 * Sort direction.
 */
export type SortOrder = 'asc' | 'desc';

/**
 * Filter option for dropdowns
 */
export interface FilterOption {
  /** Option value */
  value: string;
  /** Display label */
  label: string;
}

/**
 * Filter options response for EWS dashboard
 */
export interface EWSFilterOptions {
  /** Available batch IDs */
  batchIds: FilterOption[];
}

/**
 * Parameters for fetching customer list with pagination, filtering, and sorting.
 */
export interface GetCustomersParams {
  page: number;
  pageSize: number;
  searchQuery?: string;
  riskLevel?: RiskLevel;
  /** Batch ID to query */
  batchId?: string;
  /** Filter by specific risk indicator (e.g., "emi_bounces") */
  indicator?: string;
  /** Filter by indicator tag level (requires indicator param) */
  indicatorLevel?: RiskLevel;
  /** Column to sort by */
  sortBy?: CustomerSortColumn;
  /** Sort direction (asc or desc) */
  sortOrder?: SortOrder;
}

/**
 * Parameters for fetching overview metrics.
 */
export interface GetOverviewMetricsParams {
  /** Batch ID to query */
  batchId?: string;
}

/**
 * Parameters for fetching distributions.
 */
export interface GetDistributionsParams {
  /** Batch ID to query */
  batchId?: string;
  indicators?: string[];
  riskLevel?: RiskLevel;
}

/**
 * Risk level filter option for distributions dropdown.
 */
export type DistributionRiskFilter = 'ALL' | RiskLevel;

/**
 * API response wrapper for filter options.
 */
export interface FilterOptionsApiResponse extends ApiResponse<EWSFilterOptions> {}

// =============================================================================
// Batch Types
// =============================================================================

/**
 * Single EWS batch row from the batches/chunks tables.
 */
export interface EWSBatch {
  batch_id: string;
  status: string;
  total_chunks: number;
  total_records: number;
  created_at: string;
  completed_at: string | null;
  elapsed_seconds: number | null;
}

/**
 * Summary metrics for EWS batches.
 */
export interface EWSBatchesSummary {
  totalBatches: number;
  totalRecords: number;
  avgProcessingSeconds: number;
}

/**
 * Response data for batches list endpoint.
 */
export interface EWSBatchesResponse {
  batches: EWSBatch[];
  pagination: PaginationInfo;
  summary: EWSBatchesSummary;
}

/**
 * API response wrapper for batches list.
 */
export interface EWSBatchesApiResponse extends ApiResponse<EWSBatchesResponse> {}

/**
 * Parameters for fetching EWS batches list.
 */
export interface GetEWSBatchesParams {
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// =============================================================================
// Component Props Types
// =============================================================================

/**
 * Props for the main EarlyWarningSignalsPage component.
 * These are passed from the Angular wrapper via the component registry.
 */
export interface EarlyWarningSignalsPageProps {
  /** Route parameters extracted from the URL path */
  routeParams?: Record<string, string>;

  /** Query parameters from the URL search string */
  queryParams?: Record<string, string>;
}

/**
 * Props for the OverviewTab component.
 */
export interface OverviewTabProps {
  /** Overview metrics data (null while loading) */
  metrics: EWSOverviewMetrics | null;

  /** Whether metrics are being loaded */
  isLoading: boolean;

  /** Distribution data for analytics section */
  distributions: EWSDistributionsData | null;

  /** Whether distributions are being loaded */
  isLoadingDistributions: boolean;

  /** Currently selected risk filter for distributions */
  selectedRiskFilter: DistributionRiskFilter;

  /** Callback when risk filter changes */
  onRiskFilterChange: (filter: DistributionRiskFilter) => void;

  /** Callback when a bar in analytics chart is clicked */
  onBarClick?: (indicator: string, indicatorName: string, level: RiskLevel, levelLabel: string, count: number) => void;
}

/**
 * Props for the CustomersTab component.
 */
export interface CustomersTabProps {
  /** Current page number (1-indexed) */
  currentPage: number;

  /** Number of records per page */
  pageSize: number;

  /** Callback when page changes */
  onPageChange: (page: number) => void;

  /** Callback when page size changes */
  onPageSizeChange: (size: number) => void;

  /** Current search query */
  searchQuery: string;

  /** Current search domain */
  searchDomain: SearchDomain;

  /** Callback when search is submitted */
  onSearch: (query: string, domain: SearchDomain) => void;

  /** Customer data (null while loading) */
  customers: EWSCustomer[] | null;

  /** Pagination info */
  pagination: PaginationInfo | null;

  /** Whether data is being loaded */
  isLoading: boolean;

  /** Callback when customer row is clicked */
  onCustomerClick: (customer: EWSCustomer) => void;

  /** Current sort column */
  sortBy: CustomerSortColumn | null;

  /** Current sort direction */
  sortOrder: SortOrder;

  /** Callback when sort changes */
  onSortChange: (column: CustomerSortColumn | null, order: SortOrder) => void;
}

/**
 * Props for the EWSCustomerDrawer component.
 */
export interface EWSCustomerDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean;

  /** Callback to close the drawer */
  onClose: () => void;

  /** Customer data to display (null if no customer selected) */
  customer: EWSCustomer | null;

  /** Whether customer details are loading */
  isLoading?: boolean;

  /** Optional callback to go back (e.g., to indicator list). Shows back button when provided */
  onBack?: () => void;
}

/**
 * Props for the EWSRiskScoreDonut component.
 */
export interface EWSRiskScoreDonutProps {
  /** Risk score value (0-40 for API scale) */
  score: number;

  /** Risk level for color styling */
  riskLevel: RiskLevel;

  /** Optional size in pixels (default: 160) */
  size?: number;

  /** Maximum score value (default: 40 for API scale) */
  maxScore?: number;
}

/**
 * Props for the EWSRiskFactorsList component.
 */
export interface EWSRiskFactorsListProps {
  /** Array of risk factors to display */
  factors: EWSRiskFactor[];
}

/**
 * Props for the EWSMetricCard component.
 */
export interface EWSMetricCardProps {
  /** Title/label for the metric */
  title: string;

  /** The main value to display */
  value: string | number;

  /** Optional icon component */
  icon?: React.ReactNode;

  /** Optional background image/illustration */
  backgroundImage?: string;

  /** Optional info tooltip content */
  infoTooltip?: string;
}

/**
 * Props for the EWSRiskCard component.
 */
export interface EWSRiskCardProps {
  /** Title (e.g., "High Risk", "Critical") */
  title: string;

  /** Count of customers in this category */
  count: number;

  /** Percentage of total */
  percentage: number;

  /** Card variant for styling */
  variant: 'high' | 'critical';

  /** Callback when card is clicked */
  onClick?: () => void;

  /** Tooltip text for the info icon */
  infoTooltip?: string;
}

/**
 * Props for the EWSCustomersTable component.
 */
export interface EWSCustomersTableProps {
  /** Customer data to display */
  customers: EWSCustomer[];

  /** Whether data is loading */
  isLoading: boolean;

  /** Pagination info */
  pagination: PaginationInfo;

  /** Callback when page changes */
  onPageChange: (page: number) => void;

  /** Callback when page size changes */
  onPageSizeChange: (size: number) => void;

  /** Callback when row is clicked */
  onRowClick: (customer: EWSCustomer) => void;

  /** Current sort column (server-side sorting) */
  sortBy: CustomerSortColumn | null;

  /** Current sort direction */
  sortOrder: SortOrder;

  /** Callback when sort changes */
  onSortChange: (column: CustomerSortColumn | null, order: SortOrder) => void;
}

/**
 * Props for the EWSIndicatorCustomersDrawer component.
 * Shows paginated customer list filtered by indicator and level.
 */
export interface EWSIndicatorCustomersDrawerProps {
  /** Whether drawer is open */
  isOpen: boolean;

  /** Callback to close drawer */
  onClose: () => void;

  /** Risk indicator key (e.g., "emi_bounces") */
  indicator: string;

  /** Display name (e.g., "EMI Bounces") */
  indicatorName: string;

  /** Tag level (e.g., "HIGH") */
  level: RiskLevel;

  /** Display label (e.g., "Frequent Bounces") */
  levelLabel: string;

  /** Total count of customers matching this filter */
  totalCount: number;

  /** Batch ID to query */
  batchId?: string;

  /** Callback when a customer row is clicked */
  onCustomerClick: (customer: EWSCustomer) => void;
}

/**
 * Context for bar click event in distribution charts.
 */
export interface BarClickContext {
  indicator: string;
  indicatorName: string;
  level: RiskLevel;
  levelLabel: string;
  count: number;
}

// =============================================================================
// Utility Types and Functions
// =============================================================================

/**
 * Type guard to check if a value is a valid RiskLevel.
 */
export function isRiskLevel(value: string): value is RiskLevel {
  return ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].includes(value);
}

/**
 * Type guard to check if a value is a valid TimePeriod.
 */
export function isTimePeriod(value: string): value is TimePeriod {
  return ['lastWeek', 'lastMonth', 'last6Months', 'lastYear', 'custom'].includes(value);
}

/**
 * Get display color class for a risk level.
 * Returns Tailwind color classes.
 */
export function getRiskLevelColor(level: RiskLevel): { text: string; bg: string; border: string } {
  switch (level) {
    case 'CRITICAL':
      return {
        text: 'text-red-800',
        bg: 'bg-red-200',
        border: 'border-red-400',
      };
    case 'HIGH':
      return {
        text: 'text-red-600',
        bg: 'bg-red-100',
        border: 'border-red-300',
      };
    case 'MEDIUM':
      return {
        text: 'text-amber-600',
        bg: 'bg-amber-100',
        border: 'border-amber-300',
      };
    case 'LOW':
    default:
      return {
        text: 'text-green-600',
        bg: 'bg-green-100',
        border: 'border-green-300',
      };
  }
}

/**
 * Risk score thresholds for 0-40 scale.
 * 0-3: LOW, 4-7: MEDIUM, 8-12: HIGH, 13+: CRITICAL (treated as HIGH)
 */
export const RISK_SCORE_THRESHOLDS = {
  LOW_MAX: 3,
  MEDIUM_MAX: 7,
  HIGH_MAX: 12,
  MAX_SCORE: 40,
};

/**
 * Get risk level label based on score (0-40 scale).
 * Thresholds: 0-3 LOW, 4-7 MEDIUM, 8-12 HIGH, 13+ CRITICAL
 */
export function getRiskLevelFromScore(score: number): RiskLevel {
  if (score <= RISK_SCORE_THRESHOLDS.LOW_MAX) return 'LOW';
  if (score <= RISK_SCORE_THRESHOLDS.MEDIUM_MAX) return 'MEDIUM';
  if (score <= RISK_SCORE_THRESHOLDS.HIGH_MAX) return 'HIGH';
  return 'CRITICAL';
}

/**
 * Check if a risk score indicates critical status (13+).
 */
export function isCriticalScore(score: number): boolean {
  return score > RISK_SCORE_THRESHOLDS.HIGH_MAX;
}

/**
 * Format currency value in Indian Rupees.
 */
export function formatINR(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format large numbers with Indian numbering system.
 */
export function formatIndianNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return new Intl.NumberFormat('en-IN').format(value);
}

/**
 * Map API customer list item to internal EWSCustomer format.
 * Adds legacy field mappings for UI compatibility.
 */
export function mapCustomerListItem(item: EWSCustomerListItem): EWSCustomer {
  return {
    ...item,
    consentId: item.trackingId, // Legacy mapping
    riskScore: item.riskScore ?? 0, // Use from API response
    riskLevel: item.riskLevel ?? 'LOW', // Use from API response
    averageBalance: item.currentBalance,
    foir: item.foir ?? 0, // Use from API response
    riskFactors: [],
    emiCount: item.activeEMIsCount,
    sips: item.activeSIPs,
  };
}

/**
 * Map API customer detail to internal EWSCustomer format.
 * Ensures all legacy fields are populated.
 */
export function mapCustomerDetail(detail: EWSCustomer): EWSCustomer {
  return {
    ...detail,
    consentId: detail.trackingId, // Legacy mapping
    emiCount: detail.activeEMIsCount,
    sips: detail.activeSIPs,
  };
}
